/**
 * EL CARGADOR DEL CUADERNO (CQ4a): de Supabase a `EntradaCuaderno`, lo que el
 * motor puro (CQ2) necesita para rellenar el modelo v9 de la Junta.
 *
 *   loadCuadernoEntrada(supabase, { companyId, titularLocationId, year, allowedLocationIds? })
 *   buildCuaderno(entrada, borrador, legal)   ← puro, en build.ts; se reexporta aquí
 *
 * Lo usan la tarjeta del asistente y el botón «Modelo de la Junta (v9)» de
 * /spray-logs, con el JWT de quien mira (RLS). Reglas:
 *
 *   · D14 — el cuaderno es de UN titular (NIF) y un año. `titularLocationId` null
 *     = el titular de la empresa: todas sus fincas MENOS las que tienen otro
 *     titular en `farm_legal_profiles`; con valor = solo esa finca.
 *   · A16 — ALCANCE: la RLS de `spray_logs` es por empresa, no por finca. Un
 *     encargado acotado solo ve sus fincas (y sin los sectores que se le quitaron):
 *     se recorta con `filtrarPorAlcance`, la misma regla que la pantalla
 *     (`useLocationScope`), y las fincas del cuaderno se limitan a las suyas.
 *   · PAGINADO — PostgREST corta en 1.000 filas: partes, sectores y ciclos se
 *     piden por páginas hasta el final (un año en la demo son ~380 partes; una
 *     empresa grande pasa de mil).
 *   · COLUMNAS DE LA OLA 2 — máquina (`equipment_snapshot`, F2), aplicador
 *     (`applicator_snapshot`, F3) con su NIF leído de `company_applicators` (el
 *     NIF vive ahí, no en la copia del parte), tiempo exterior (`weather_*`, F1)
 *     y eficacia (`efficacy`, F123) si la columna ya existe: si todavía no está,
 *     se pide sin ella y el motor deja la eficacia como hueco (nunca inventada).
 *   · LO QUE EL PARTE NO GUARDÓ (CU2, 27-09-2026): los partes de antes del 26-09
 *     no tienen copia de máquina ni de aplicador. Se DEDUCEN con la regla del
 *     servidor al completar (`pickEquipment`, `pickApplicator`: el asignado de la
 *     tarea → quien lo registró, con su carné vivo; la máquina por defecto de la
 *     finca → la de la empresa → la única activa) y van marcados en
 *     `parte.deducido`: el motor los imprime con origen «deducido» (confírmalo).
 *   · El catálogo del MAPA y el interruptor D26 (`registro_mapa_off`) se pueden
 *     pasar hechos; si no, se piden aquí (el interruptor, por la RPC
 *     `_registro_mapa_off`, igual que la edge).
 */
import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { filtrarPorAlcance } from "@/lib/alcance";
import { equipmentLabel, equipmentRegime, pickEquipment, type EquipmentRow } from "@/lib/machinery/equipmentRules";
import { pickApplicator, type ApplicatorRow } from "@/lib/applicators/applicatorRules";
import type { RegistryItem } from "@/lib/registrySuggest";
import {
  CONDICIONES_TIEMPO,
  EFICACIAS,
  type AplicadorEntrada,
  type CicloEntrada,
  type CondicionTiempo,
  type Eficacia,
  type EntradaCuaderno,
  type EquipoEntrada,
  type FincaEntrada,
  type LineaEntrada,
  type NaveEntrada,
  type ParteEntrada,
  type ProductoInventario,
  type RecintoEntrada,
  type SectorEntrada,
} from "./entrada";
import type { DatosLegalesRow } from "./datosLegales";
import { checkRoma, checkRopo } from "./validators";
import type { BorradorCuaderno } from "./borrador";

export { buildCuaderno } from "./build";
export type { CuadernoBuild, ParcelaCuaderno } from "./build";

// ─────────────────────────── el cliente, en lo mínimo que se usa ───────────────────────────

interface Respuesta {
  data: unknown;
  error: { message?: string; code?: string; details?: string | null } | null;
}
interface Consulta extends PromiseLike<Respuesta> {
  select(cols: string): Consulta;
  eq(col: string, v: unknown): Consulta;
  in(col: string, v: unknown[]): Consulta;
  gte(col: string, v: unknown): Consulta;
  lte(col: string, v: unknown): Consulta;
  or(filtro: string): Consulta;
  order(col: string, o?: { ascending?: boolean }): Consulta;
  range(desde: number, hasta: number): Consulta;
  limit(n: number): Consulta;
  maybeSingle(): PromiseLike<Respuesta>;
}
/** Lo que el cargador usa del cliente de Supabase (así un test le pasa uno falso). */
export interface ClienteCuaderno {
  from(tabla: string): Consulta;
  /** Solo para `_registro_mapa_off` (D26). Opcional: un cliente de prueba sin él lee «encendido». */
  rpc?(fn: string, args?: Record<string, unknown>): PromiseLike<Respuesta>;
  functions?: { invoke(nombre: string, opts?: unknown): Promise<{ data: unknown; error: unknown }> };
}
type Sb = SupabaseClient<Database> | ClienteCuaderno;
const cliente = (s: Sb) => s as unknown as ClienteCuaderno;

/** Tamaño de página: el tope de PostgREST. */
export const PAGINA = 1000;

type Fila = Record<string, unknown>;

class ErrorCarga extends Error {
  constructor(
    public readonly tabla: string,
    public readonly causa: Respuesta["error"],
  ) {
    super(`cuaderno: no se ha podido leer ${tabla}${causa?.message ? ` (${causa.message})` : ""}`);
  }
}

async function una(tabla: string, q: PromiseLike<Respuesta>): Promise<Fila[]> {
  const { data, error } = await q;
  if (error) throw new ErrorCarga(tabla, error);
  return (Array.isArray(data) ? data : data ? [data] : []) as Fila[];
}

/** Todas las páginas de una consulta, hasta que una vuelve corta. */
async function todas(tabla: string, pagina: (desde: number, hasta: number) => PromiseLike<Respuesta>, tam = PAGINA): Promise<Fila[]> {
  const out: Fila[] = [];
  for (let desde = 0; ; desde += tam) {
    const filas = await una(tabla, pagina(desde, desde + tam - 1));
    out.push(...filas);
    if (filas.length < tam) return out;
  }
}

// ─────────────────────────── lectura tolerante de columnas ───────────────────────────

const str = (v: unknown): string | null => {
  if (v == null) return null;
  const s = String(v).trim();
  return s ? s : null;
};
const numero = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};
/** Código SIEX (`crop_code`, F4 cultivos SIEX): entero positivo o null (columna ausente o vacía). */
const codigoSiex = (v: unknown): number | null => {
  const n = numero(v);
  return n != null && Number.isInteger(n) && n > 0 ? n : null;
};
const objeto = (v: unknown): Fila => (v && typeof v === "object" && !Array.isArray(v) ? (v as Fila) : {});
const CARNES = ["basico", "cualificado", "fumigador", "piloto"] as const;
const carneDe = (v: unknown): AplicadorEntrada["carne"] => {
  const s = str(v);
  return (CARNES as readonly string[]).includes(s ?? "") ? (s as AplicadorEntrada["carne"]) : null;
};
const CONDICIONES: readonly CondicionTiempo[] = CONDICIONES_TIEMPO;

// ─────────────────────────── de la fila de Pest a la entrada del motor ───────────────────────────

/** Máquina del parte (F2): la COPIA del día manda; si no hay copia, la ficha de hoy. */
export function equipoDeParte(log: Fila, porId: Map<string, Fila>): EquipoEntrada | null {
  const snap = objeto(log.equipment_snapshot);
  const id = str(log.equipment_id) ?? str(snap.id);
  if (!id) return null;
  const fila = porId.get(id);
  return equipoDeFila({ ...(fila ?? {}), id }, snap);
}

/**
 * Una máquina de `company_equipment` (y la copia del parte, si la hay) como la ve
 * el motor. Exportada para la página «Cuaderno de campo» de Ajustes, que cuenta
 * los huecos con esta MISMA lectura del ROMA (`progresoAjustes`).
 */
export function equipoDeFila(fila: Fila, snap: Fila = {}): EquipoEntrada {
  const tipo = str(snap.tipo) ?? str(fila.equipment_type);
  const regimen = str(snap.regimen) ?? equipmentRegime(tipo);
  const descripcion =
    str(snap.descripcion) ??
    (str(fila.description) ? equipmentLabel({ description: String(fila.description), brand: str(fila.brand), model: str(fila.model) }) : null) ??
    "—";
  /* La copia del día manda si su nº VALE; si no lo tiene (o no vale), el de la ficha de
     hoy: un ROMA dado de alta después (en Ajustes o desde el asistente, CU7) cierra el
     hueco y el cuaderno no lo vuelve a preguntar. */
  const roma =
    regimen === "exenta"
      ? "Exenta"
      : regimen === "censo"
        ? primeroQueVale([snap.censo, fila.census_reference, snap.roma, fila.roma_number], checkRoma)
        : primeroQueVale([snap.roma, fila.roma_number], checkRoma);
  return {
    id: String(fila.id),
    descripcion,
    roma,
    fechaAdquisicion: str(snap.adquirida) ?? str(fila.acquired_on),
    fechaUltimaInspeccion: str(snap.ultima_inspeccion) ?? str(fila.last_inspection_on),
  };
}

/** Un aplicador de `company_applicators` como lo ve el motor (también lo usa la página de Ajustes). */
export function aplicadorDeFila(f: Fila): AplicadorEntrada {
  return {
    id: String(f.id),
    nombre: str(f.display_name) ?? "",
    nif: str(f.nif),
    ropo: str(f.ropo_number),
    carne: carneDe(f.license_type),
  };
}

/** El primer valor que pasa la comprobación; si ninguno, el primero que hay (el hueco dirá «no vale»). */
function primeroQueVale(valores: unknown[], comprobar: (v: string) => { ok: boolean }): string | null {
  const hay = valores.map(str).filter((v): v is string => v != null);
  return hay.find((v) => comprobar(v).ok) ?? hay[0] ?? null;
}

/**
 * Aplicador del parte (F3): nombre, ROPO y carné de la COPIA del día; el NIF, de
 * `company_applicators` (vive solo ahí). Una persona sin carné sigue siendo el
 * aplicador (`persona:<uid>`): el motor pregunta su carné, no busca a otra.
 *
 * CU7 (27-09-2026): si a esa PERSONA se le da de alta el carné después (en su
 * ficha o desde el asistente), el parte la encuentra por `user_id` (`porPersona`,
 * solo carnés vivos) y el cuaderno deja de preguntarlo; y un ROPO que falta o no
 * vale en la copia se toma de la ficha de hoy.
 */
export function aplicadorDeParte(log: Fila, porId: Map<string, Fila>, porPersona?: Map<string, Fila>): AplicadorEntrada | null {
  const snap = objeto(log.applicator_snapshot);
  const idCarne = str(log.applicator_id) ?? str(snap.id);
  const persona = str(log.applicator_user_id) ?? str(snap.user_id);
  const fila = idCarne ? porId.get(idCarne) : persona ? porPersona?.get(persona) : undefined;
  const id = idCarne ?? (fila ? String(fila.id) : persona ? `persona:${persona}` : null);
  if (!id) return null;
  return {
    id,
    nombre: str(snap.nombre) ?? str(fila?.display_name) ?? "",
    nif: str(fila?.nif) ?? str(snap.nif),
    ropo: primeroQueVale([snap.ropo, fila?.ropo_number], checkRopo),
    carne: carneDe(snap.carne) ?? carneDe(fila?.license_type),
  };
}

// ─────────────────────────── lo que el parte no guardó (CU2) ───────────────────────────

/**
 * Lo que hace falta para DEDUCIR la máquina y el aplicador de un parte que no los
 * guardó (los de antes del 26-09-2026, F2/F3): las filas vivas de la empresa, la
 * tarea de cada parte (quién estaba asignado y lo elegido en ella) y la finca de
 * cada nave. Lo arma `loadCuadernoEntrada`; un test lo arma a mano.
 */
export interface ContextoDeduccion {
  /** Filas de `company_equipment` (con `is_default`): `pickEquipment` descarta las no vivas. */
  equipos: Fila[];
  /** Filas de `company_applicators`: `pickApplicator` descarta las no vivas. */
  aplicadores: Fila[];
  /**
   * Tarea por id (`assigned_to`, `applicator_id`, `equipment_id`). null = no se pudo
   * leer: entonces el aplicador de un parte CON tarea no se deduce (no se sabe a quién
   * estaba asignada y «quien lo registró» podría no ser quien aplicó).
   */
  tareas: Map<string, Fila> | null;
  /** Nave → finca (para la máquina por defecto de la finca). */
  fincaDeNave: Map<string, string | null>;
  /** Nombre de una persona SIN carné (`profiles.full_name`, nunca el correo: E11). */
  nombrePersona?: Map<string, string>;
}

/** La nave del parte: la suya o la de su sector. */
const naveDeLog = (log: Fila): string | null => str(log.greenhouse_id) ?? str(objeto(log.blocks).greenhouse_id);

/**
 * MÁQUINA DEDUCIDA (T14, la cadena del servidor al completar, `pickEquipment`): la
 * elegida en la tarea → la de por defecto de la finca de la nave → la de por defecto
 * de la empresa → la ÚNICA activa que se puede usar ahí. Si no hay una clara, null
 * (hueco): nunca se elige una al azar.
 */
export function deducirEquipo(log: Fila, ctx: ContextoDeduccion): EquipoEntrada | null {
  const taskId = str(log.task_id);
  const tarea = taskId ? ctx.tareas?.get(taskId) : undefined;
  const nave = naveDeLog(log);
  const e = pickEquipment(ctx.equipos as unknown as EquipmentRow[], {
    chosenId: str(tarea?.equipment_id),
    locationId: nave ? ctx.fincaDeNave.get(nave) ?? null : null,
  });
  return e ? equipoDeFila(e as unknown as Fila) : null;
}

/**
 * APLICADOR DEDUCIDO (T15, la cadena del servidor, `pickApplicator`): el elegido en
 * la tarea → el ASIGNADO a la tarea → quien registró el parte (`created_by`), con su
 * carné VIVO de `company_applicators` (NIF incluido). Una persona sin carné sigue
 * siendo el aplicador (`persona:<uid>`, como en `aplicadorDeParte`): el cuaderno
 * pregunta su carné, no busca a otra. Parte con tarea que no se pudo leer: null.
 */
export function deducirAplicador(log: Fila, ctx: ContextoDeduccion): AplicadorEntrada | null {
  const taskId = str(log.task_id);
  if (taskId && !ctx.tareas?.has(taskId)) return null;
  const tarea = taskId ? ctx.tareas?.get(taskId) : undefined;
  const picked = pickApplicator(ctx.aplicadores as unknown as ApplicatorRow[], {
    chosenId: str(tarea?.applicator_id),
    assignedUserId: str(tarea?.assigned_to),
    completedByUserId: str(log.created_by),
  });
  if (picked.applicator) return aplicadorDeFila(picked.applicator as unknown as Fila);
  if (!picked.userId) return null;
  return { id: `persona:${picked.userId}`, nombre: ctx.nombrePersona?.get(picked.userId) ?? "", nif: null, ropo: null, carne: null };
}

export function tiempoDeParte(log: Fila): ParteEntrada["tiempo"] {
  const c = str(log.weather_condition) as CondicionTiempo | null;
  if (!c || !CONDICIONES.includes(c)) return null;
  const s = str(log.weather_source);
  return { condicion: c, fuente: s === "auto" ? "medido" : s === "default" ? "default" : "manual" };
}

/**
 * @param deducir si viene, lo que el parte NO guardó (máquina, aplicador) se deduce
 *   con la regla del servidor y queda marcado en `deducido` (CU2). Sin él, solo lo guardado.
 */
export function parteDeFila(
  log: Fila,
  equipos: Map<string, Fila>,
  aplicadores: Map<string, Fila>,
  aplicadoresPorPersona?: Map<string, Fila>,
  deducir?: ContextoDeduccion,
): ParteEntrada {
  const ef = str(log.efficacy) as Eficacia | null;
  const efFuente = str(log.efficacy_source);
  let equipo = equipoDeParte(log, equipos);
  let aplicador = aplicadorDeParte(log, aplicadores, aplicadoresPorPersona);
  const deducido: NonNullable<ParteEntrada["deducido"]> = {};
  if (deducir && !equipo) {
    equipo = deducirEquipo(log, deducir);
    if (equipo) deducido.equipo = true;
  }
  if (deducir && !aplicador) {
    aplicador = deducirAplicador(log, deducir);
    if (aplicador) deducido.aplicador = true;
  }
  return {
    id: String(log.id),
    fecha: String(log.date ?? "").slice(0, 10),
    naveId: naveDeLog(log),
    blockId: str(log.block_id),
    cropCycleId: str(log.crop_cycle_id),
    areaM2: numero(log.block_area_m2_snapshot),
    notas: str(log.notes),
    taskId: str(log.task_id),
    equipo,
    aplicador,
    ...(deducido.equipo || deducido.aplicador ? { deducido } : {}),
    tiempo: tiempoDeParte(log),
    metodo: null,
    eficacia: ef && EFICACIAS.includes(ef) ? ef : null,
    eficaciaFuente: efFuente === "defecto" || efFuente === "usuario" ? efFuente : null,
    creadoEn: str(log.created_at),
  };
}

export function lineaDeFila(e: Fila, parteId: string, inventario: Map<string, Fila>): LineaEntrada {
  const productoId = str(e.product_id) ?? str(e.inventory_item_id);
  const inv = productoId ? inventario.get(productoId) : undefined;
  return {
    id: String(e.id),
    parteId,
    productoId,
    nombre: str(e.product_name_snapshot) ?? str(inv?.product_name) ?? str(e.treatment_name_snapshot) ?? "—",
    categoria: str(e.category_snapshot) ?? str(inv?.category),
    registro: str(e.registration_number_snapshot),
    lote: str(e.batch_number_snapshot),
    plaga: str(e.pest_name_snapshot) ?? str(objeto(e.pests).name),
    cantidad: numero(e.amount_used),
    unidad: str(e.unit) ?? str(e.unit_snapshot),
    dosisValor: numero(e.applied_dose_value),
    dosisUnidad: str(e.applied_dose_unit),
    agua: numero(e.water_volume_l),
    aguaUnidad: str(e.water_volume_unit),
  };
}

// ─────────────────────────── la carga ───────────────────────────

export interface OpcionesCarga {
  companyId: string;
  /** null = el titular de la empresa (D14). */
  titularLocationId: string | null;
  year: number;
  /** `useLocationScope().allowedLocationIds`: null = sin recorte. */
  allowedLocationIds?: Set<string> | null;
  /** `useLocationScope().excludedBlockIds`. */
  excludedBlockIds?: Set<string> | null;
  /** Opción de campaña (D14): si viene, manda sobre el año natural. */
  campana?: EntradaCuaderno["campana"];
  /** Catálogo del MAPA ya pedido; undefined = se pide aquí; null = sin catálogo. */
  catalogo?: RegistryItem[] | null;
  /** D26; undefined = se pregunta aquí a la RPC `_registro_mapa_off`. */
  mapaValidation?: boolean;
  /** Solo tests. */
  pagina?: number;
}

const SELECT_PARTE_BASE =
  "id, date, greenhouse_id, block_id, crop_cycle_id, block_area_m2_snapshot, notes, task_id, " +
  "equipment_id, equipment_snapshot, applicator_id, applicator_user_id, applicator_snapshot, " +
  "weather_condition, weather_source, created_at, created_by";
const SELECT_EFICACIA = "efficacy, efficacy_source";
const SELECT_PARTE_RESTO =
  "greenhouses(location_id), blocks(greenhouse_id, greenhouses(location_id)), " +
  "spray_log_entries(id, product_id, inventory_item_id, product_name_snapshot, treatment_name_snapshot, category_snapshot, " +
  "registration_number_snapshot, batch_number_snapshot, pest_name_snapshot, amount_used, unit, unit_snapshot, " +
  "applied_dose_value, applied_dose_unit, water_volume_l, water_volume_unit, pests(name))";

/** ¿El error es «esa columna no existe» (la eficacia llega con F123)? */
const faltaColumnaEficacia = (e: unknown) => {
  const x = (e as ErrorCarga)?.causa ?? (e as Respuesta["error"]);
  return !!x && (x.code === "42703" || x.code === "PGRST204" || /efficacy/i.test(x.message ?? ""));
};

export async function loadCuadernoEntrada(supabase: Sb, o: OpcionesCarga): Promise<EntradaCuaderno> {
  const db = cliente(supabase);
  const tam = o.pagina ?? PAGINA;
  const periodo = o.campana
    ? { desde: o.campana.desde, hasta: o.campana.hasta }
    : { desde: `${o.year}-01-01`, hasta: `${o.year}-12-31` };

  const partesDe = (conEficacia: boolean) =>
    todas(
      "spray_logs",
      (desde, hasta) =>
        db
          .from("spray_logs")
          .select([SELECT_PARTE_BASE, conEficacia ? SELECT_EFICACIA : null, SELECT_PARTE_RESTO].filter(Boolean).join(", "))
          .eq("company_id", o.companyId)
          .gte("date", periodo.desde)
          .lte("date", periodo.hasta)
          .order("date", { ascending: true })
          .order("id", { ascending: true })
          .range(desde, hasta),
      tam,
    );
  const partesCrudas = partesDe(true).catch((e) => {
    if (faltaColumnaEficacia(e)) return partesDe(false);
    throw e;
  });

  const [fincasTodas, perfiles, navesTodas, sectoresTodos, ciclosTodos, recintosTodos, equiposFilas, aplicadoresFilas, mapaOff, catalogo, logs] =
    await Promise.all([
      una("locations", db.from("locations").select("id, name").eq("company_id", o.companyId)),
      una("farm_legal_profiles", db.from("farm_legal_profiles").select("*").eq("company_id", o.companyId)),
      una("greenhouses", db.from("greenhouses").select("id, name, location_id").eq("company_id", o.companyId)),
      todas(
        "blocks",
        (d, h) =>
          db.from("blocks").select("id, greenhouse_id, name, area_m2, crop_name, crop_code, variety_name").eq("company_id", o.companyId).order("id").range(d, h),
        tam,
      ),
      todas(
        "crop_cycles",
        (d, h) =>
          db
            .from("crop_cycles")
            .select("id, block_id, crop_name, crop_code, variety_name, start_date, end_date")
            .eq("company_id", o.companyId)
            .lte("start_date", periodo.hasta)
            .or(`end_date.is.null,end_date.gte.${periodo.desde}`)
            .order("id")
            .range(d, h),
        tam,
      ),
      una(
        "greenhouse_sigpac_recintos",
        db
          .from("greenhouse_sigpac_recintos")
          // Todas las columnas (25-10-2026): `parte_nave` es nueva y una lista cerrada
          // rompería la carga en una base sin la migración `20261025090000`.
          .select("*")
          .eq("company_id", o.companyId),
      ),
      una(
        "company_equipment",
        db
          .from("company_equipment")
          .select("id, location_id, equipment_type, description, brand, model, roma_number, census_reference, acquired_on, last_inspection_on, is_default, active, archived_at")
          .eq("company_id", o.companyId),
      ),
      una(
        "company_applicators",
        db
          .from("company_applicators")
          .select("id, user_id, kind, display_name, nif, ropo_number, license_type, active, archived_at")
          .eq("company_id", o.companyId),
      ),
      o.mapaValidation !== undefined ? Promise.resolve(!o.mapaValidation) : leerMapaOff(db, o.companyId),
      o.catalogo !== undefined ? Promise.resolve(o.catalogo) : pedirCatalogo(db),
      partesCrudas,
    ]);

  // ── El titular (D14) y el alcance (A16) ───────────────────────────────────
  const legal = (perfiles.find((p) => (str(p.location_id) ?? null) === (o.titularLocationId ?? null)) ?? null) as DatosLegalesRow | null;
  const deOtroTitular = new Set(perfiles.map((p) => str(p.location_id)).filter(Boolean));
  const alcance = o.allowedLocationIds ?? null;
  const fincas: FincaEntrada[] = fincasTodas
    .filter((f) => (o.titularLocationId ? f.id === o.titularLocationId : !deOtroTitular.has(String(f.id))))
    .filter((f) => !alcance || alcance.has(String(f.id)))
    .map((f) => ({ id: String(f.id), nombre: str(f.name) ?? "" }));
  const fincaIds = new Set(fincas.map((f) => f.id));
  const naves: NaveEntrada[] = navesTodas
    .filter((g) => (str(g.location_id) ? fincaIds.has(String(g.location_id)) : !o.titularLocationId))
    .map((g) => ({ id: String(g.id), nombre: str(g.name) ?? "", locationId: str(g.location_id) }));
  const naveIds = new Set(naves.map((n) => n.id));
  const quitados = o.excludedBlockIds ?? null;
  const sectores: SectorEntrada[] = sectoresTodos
    .filter((b) => naveIds.has(String(b.greenhouse_id)) && !quitados?.has(String(b.id)))
    .map((b) => ({
      id: String(b.id),
      naveId: String(b.greenhouse_id),
      nombre: str(b.name),
      areaM2: numero(b.area_m2),
      cultivo: str(b.crop_name),
      cultivoCodigo: codigoSiex(b.crop_code),
      variedad: str(b.variety_name),
    }));
  const sectorIds = new Set(sectores.map((s) => s.id));
  const ciclos: CicloEntrada[] = ciclosTodos
    .filter((c) => str(c.block_id) && sectorIds.has(String(c.block_id)) && str(c.crop_name) && str(c.start_date))
    .map((c) => ({
      id: String(c.id),
      blockId: str(c.block_id),
      especie: String(c.crop_name).trim(),
      especieCodigo: codigoSiex(c.crop_code),
      variedad: str(c.variety_name),
      inicio: String(c.start_date).slice(0, 10),
      fin: str(c.end_date)?.slice(0, 10) ?? null,
    }));
  const recintos: RecintoEntrada[] = recintosTodos
    .filter((r) => naveIds.has(String(r.greenhouse_id)))
    .map((r) => ({
      greenhouseId: String(r.greenhouse_id),
      provincia: String(r.provincia ?? ""),
      municipioCodigo: String(r.municipio_codigo ?? ""),
      municipioNombre: str(r.municipio_nombre),
      agregado: String(r.agregado ?? "0"),
      zona: String(r.zona ?? "0"),
      poligono: String(r.poligono ?? ""),
      parcela: String(r.parcela ?? ""),
      recinto: String(r.recinto ?? ""),
      usoSigpac: str(r.uso_sigpac),
      superficieHa: numero(r.superficie_ha),
      confirmadoEn: str(r.confirmado_en),
      parteNave: numero(r.parte_nave),
      muestras: numero(r.muestras),
    }));

  // La RLS de spray_logs es de EMPRESA: el recorte por fincas lo pone esto (A16).
  const partesEnAlcance = filtrarPorAlcance(logs, alcance, quitados);

  // ── Maquinaria y carnés ───────────────────────────────────────────────────
  const equipos = new Map(equiposFilas.map((f) => [String(f.id), f]));
  const aplicadores = new Map(aplicadoresFilas.map((f) => [String(f.id), f]));
  const vivo = (f: Fila) => f.active !== false && !f.archived_at;
  // El carné VIVO de cada persona (uno por persona, índice único de la tabla): CU7.
  const aplicadoresPorPersona = new Map(
    aplicadoresFilas.filter((f) => vivo(f) && str(f.kind) !== "service_company" && str(f.user_id)).map((f) => [String(f.user_id), f]),
  );
  const equiposEmpresa = equiposFilas.filter(vivo).map((f) => equipoDeFila(f));
  const aplicadoresEmpresa: AplicadorEntrada[] = aplicadoresFilas.filter(vivo).map(aplicadorDeFila);

  // ── Lo que los partes no guardaron (CU2): tareas y nombres para deducirlo ──
  const delCuaderno = partesEnAlcance.filter((log) => {
    const n = naveDeLog(log);
    return !!n && naveIds.has(n);
  });
  const deducir = await contextoDeduccion(db, {
    logs: delCuaderno,
    equiposFilas,
    aplicadoresFilas,
    fincaDeNave: new Map(navesTodas.map((g) => [String(g.id), str(g.location_id)])),
    equipos,
    aplicadores,
    aplicadoresPorPersona,
  });

  // ── Partes y líneas (solo las del cuaderno) ───────────────────────────────
  const partes: ParteEntrada[] = [];
  const lineasCrudas: { e: Fila; parteId: string }[] = [];
  for (const log of delCuaderno) {
    const p = parteDeFila(log, equipos, aplicadores, aplicadoresPorPersona, deducir);
    if (!p.naveId || !naveIds.has(p.naveId)) continue;
    partes.push(p);
    for (const e of (Array.isArray(log.spray_log_entries) ? log.spray_log_entries : []) as Fila[]) lineasCrudas.push({ e, parteId: p.id });
  }

  const productoIds = [...new Set(lineasCrudas.map(({ e }) => str(e.product_id) ?? str(e.inventory_item_id)).filter(Boolean))] as string[];
  const inventarioFilas: Fila[] = [];
  for (let i = 0; i < productoIds.length; i += 150) {
    inventarioFilas.push(
      ...(await una(
        "inventory",
        db.from("inventory").select("id, product_name, registration_number, unit, category").in("id", productoIds.slice(i, i + 150)),
      )),
    );
  }
  const inventarioPorId = new Map(inventarioFilas.map((f) => [String(f.id), f]));
  const lineas = lineasCrudas.map(({ e, parteId }) => lineaDeFila(e, parteId, inventarioPorId));
  const inventario: ProductoInventario[] = inventarioFilas.map((f) => ({
    id: String(f.id),
    registro: str(f.registration_number),
    unidad: str(f.unit),
    categoria: str(f.category),
  }));

  return {
    anio: o.year,
    campana: o.campana ?? null,
    legal,
    // Las fincas ya vienen recortadas (titular y alcance): el motor no filtra otra vez.
    fincasDelTitular: null,
    fincas,
    naves,
    sectores,
    ciclos,
    recintos,
    partes,
    lineas,
    inventario,
    catalogo: mapaOff ? null : catalogo,
    mapaValidation: !mapaOff,
    equiposEmpresa,
    aplicadoresEmpresa,
  };
}

/**
 * CU2: arma el `ContextoDeduccion` leyendo SOLO lo que hace falta: las tareas de los
 * partes a los que les falta el aplicador o la máquina (por lotes de 150, `id in`) y
 * el nombre de las personas sin carné que saldrían como aplicador. Una lectura que
 * falla no rompe el cuaderno: sin tareas, el aplicador de un parte con tarea no se
 * deduce (hueco, como antes); sin nombres, sale sin nombre.
 */
async function contextoDeduccion(
  db: ClienteCuaderno,
  o: {
    logs: Fila[];
    equiposFilas: Fila[];
    aplicadoresFilas: Fila[];
    fincaDeNave: Map<string, string | null>;
    equipos: Map<string, Fila>;
    aplicadores: Map<string, Fila>;
    aplicadoresPorPersona: Map<string, Fila>;
  },
): Promise<ContextoDeduccion> {
  const ctx: ContextoDeduccion = { equipos: o.equiposFilas, aplicadores: o.aplicadoresFilas, tareas: new Map(), fincaDeNave: o.fincaDeNave };
  const faltan = o.logs.filter(
    (log) => !equipoDeParte(log, o.equipos) || !aplicadorDeParte(log, o.aplicadores, o.aplicadoresPorPersona),
  );
  const taskIds = [...new Set(faltan.map((l) => str(l.task_id)).filter(Boolean))] as string[];
  try {
    for (let i = 0; i < taskIds.length; i += 150) {
      const filas = await una(
        "planner_tasks",
        db.from("planner_tasks").select("id, assigned_to, applicator_id, equipment_id").in("id", taskIds.slice(i, i + 150)),
      );
      for (const t of filas) ctx.tareas?.set(String(t.id), t);
    }
  } catch {
    ctx.tareas = null;
  }
  // Las personas SIN carné vivo que saldrían como aplicador: su nombre, de su perfil.
  const conCarne = new Set(o.aplicadoresPorPersona.keys());
  const personas = new Set<string>();
  for (const log of faltan) {
    if (aplicadorDeParte(log, o.aplicadores, o.aplicadoresPorPersona)) continue;
    const a = deducirAplicador(log, ctx);
    if (a?.id.startsWith("persona:")) {
      const uid = a.id.slice("persona:".length);
      if (!conCarne.has(uid)) personas.add(uid);
    }
  }
  if (personas.size) {
    const nombres = new Map<string, string>();
    try {
      const ids = [...personas];
      for (let i = 0; i < ids.length; i += 150) {
        const filas = await una("profiles", db.from("profiles").select("user_id, full_name").in("user_id", ids.slice(i, i + 150)));
        for (const f of filas) {
          const n = str(f.full_name);
          if (n && str(f.user_id)) nombres.set(String(f.user_id), n);
        }
      }
    } catch {
      /* sin nombres: la persona sale sin nombre y el hueco del carné lo pregunta */
    }
    ctx.nombrePersona = nombres;
  }
  return ctx;
}

/**
 * D26: ¿tiene la empresa el Registro MAPA apagado? Por la RPC `_registro_mapa_off`
 * (SECURITY DEFINER, la misma que usan la edge del asistente, el escáner y el alta),
 * NO leyendo la fila de `platform_settings`: su lectura para `authenticated` es una
 * lista de claves (tanda 1 de seguridad) y, si la clave faltara, la lectura daría
 * «sin fila» = encendido sin avisar (B1 de la revisión de la ola 3). Si no se
 * puede leer, encendido (lo de siempre: el MAPA valida).
 */
async function leerMapaOff(db: ClienteCuaderno, companyId: string): Promise<boolean> {
  try {
    if (!db.rpc) return false;
    const { data, error } = await db.rpc("_registro_mapa_off", { p_company: companyId });
    return !error && data === true;
  } catch {
    return false;
  }
}

/** El catálogo del MAPA (`phyto-registry-catalog`). Sin él, null: la guarda de salida lo dirá. */
async function pedirCatalogo(db: ClienteCuaderno): Promise<RegistryItem[] | null> {
  try {
    if (!db.functions) return null;
    const { data, error } = await db.functions.invoke("phyto-registry-catalog");
    if (error) return null;
    const items = objeto(data).items;
    return Array.isArray(items) && items.length ? (items as RegistryItem[]) : null;
  } catch {
    return null;
  }
}

// ─────────────────────────── el borrador y los titulares ───────────────────────────

/**
 * El borrador de ese titular y año, o null si todavía no hay (lo crea
 * `ensure_cuaderno_draft` al primer cambio o descarga). Se busca SIEMPRE por la
 * terna (empresa, titular, año), la misma de la RPC y del índice único: un id
 * que traiga una tarjeta vieja del chat no manda (podría ser de un borrador que
 * ya no existe o de otro titular).
 */
export async function cargarBorrador(
  supabase: Sb,
  o: { companyId: string; titularLocationId: string | null; year: number },
): Promise<BorradorCuaderno | null> {
  const db = cliente(supabase);
  let q = db
    .from("cuaderno_drafts")
    /* `*` y no la lista: `line_edits` y `added_lines` (CU9, migración 20260928090000) pueden no existir todavía. */
    .select("*")
    .eq("company_id", o.companyId)
    .eq("year", o.year);
  q = o.titularLocationId ? q.eq("titular_location_id", o.titularLocationId) : q.or("titular_location_id.is.null");
  const { data, error } = await q.maybeSingle();
  if (error) throw new ErrorCarga("cuaderno_drafts", error);
  return (data as BorradorCuaderno) ?? null;
}

export interface TitularCuaderno {
  /** null = el titular de la empresa. */
  locationId: string | null;
  /** Razón social o titular guardados; si no hay, el nombre de la finca. */
  nombre: string | null;
}

/** Los titulares de la empresa (D14): el de la empresa y una finca por cada OTRO titular. */
export async function listarTitulares(supabase: Sb, companyId: string): Promise<TitularCuaderno[]> {
  const db = cliente(supabase);
  const [perfiles, fincas] = await Promise.all([
    una("farm_legal_profiles", db.from("farm_legal_profiles").select("location_id, razon_social, titular_nombre").eq("company_id", companyId)),
    una("locations", db.from("locations").select("id, name").eq("company_id", companyId)),
  ]);
  const nombreFinca = new Map(fincas.map((f) => [String(f.id), str(f.name)]));
  const empresa = perfiles.find((p) => !str(p.location_id));
  const out: TitularCuaderno[] = [{ locationId: null, nombre: str(empresa?.razon_social) ?? str(empresa?.titular_nombre) }];
  for (const p of perfiles) {
    const loc = str(p.location_id);
    if (!loc) continue;
    out.push({ locationId: loc, nombre: str(p.razon_social) ?? str(p.titular_nombre) ?? nombreFinca.get(loc) ?? null });
  }
  return out;
}
